// veeam-server-architecture.js — Named Veeam server instance management.
// Handles VB365, Veeam Backup for AWS/Azure/GCP, and Kasten K10 named server instances
// per site: add, remove, update field, and sync to existingEnvironment counts.
//
// Window bridges consumed: drawerServerVersions, syncDesignOptionFields,
//   updateNonGfNameBarCount, updateGfSummaryCount, updateTotalInstances,
//   updateWorkloadValidationState, syncNextStepAvailability
// Window bridges set: syncExistingEnvironmentFromServers

import { AppState } from './state.js';
import { getSiteById, createVeeamServerInstance } from './helpers.js';
import { renderScopeMatrix, syncMatrixToHiddenInputs } from './scope-matrix.js';
import { VEEAM_SERVER_TYPES, createEmptyExistingEnvironment } from './recalc.js';
import { emit, BUS_EVENTS } from './bus.js';

/**
 * Sync existingEnvironment counts from the named veeamServers array.
 * VBR is managed exclusively as a count chip (existingEnvironment.vbrServers)
 * via addComponentToSiteFromDrawer, so it is intentionally NOT derived here.
 * Extend this function when VB365, Kasten, etc. gain their own existingEnvironment keys.
 */
export function syncExistingEnvironmentFromServers(site) {
    if (!Array.isArray(site.veeamServers)) return;
    if (!site.existingEnvironment || typeof site.existingEnvironment !== 'object') {
        site.existingEnvironment = createEmptyExistingEnvironment();
    }
    // VBR is managed exclusively as a count chip via addComponentToSiteFromDrawer
    // (stored in existingEnvironment.vbrServers). It is never added to the veeamServers
    // array, so we must NOT derive vbrServers from that array — doing so would zero out
    // the VBR chip whenever any other named server type (e.g. VB365) is added.
    // (VB365, Kasten, etc. don't yet have existingEnvironment keys — extend here when added)
}
window.syncExistingEnvironmentFromServers = syncExistingEnvironmentFromServers;

export function addVeeamServerToSite(siteId, serverType) {
    const site = getSiteById(siteId);
    if (!site) return;
    if (!Array.isArray(site.veeamServers)) site.veeamServers = [];
    const typeDef = VEEAM_SERVER_TYPES[serverType];
    if (!typeDef) return;
    // Default name: "VBR-01", "VBR-02", etc.
    const existingOfType = site.veeamServers.filter((s) => s.type === serverType).length;
    const instanceLabel = typeDef.label;
    const suffix = String(existingOfType + 1).padStart(2, '0');
    const server = createVeeamServerInstance(serverType, `${instanceLabel}-${suffix}`);
    site.veeamServers.push(server);
    // Store the selected drawer version for this server type on the site
    if (Object.hasOwn(window.drawerServerVersions, serverType)) {
        if (!site.existingEnvironmentServerVersions || typeof site.existingEnvironmentServerVersions !== 'object') {
            site.existingEnvironmentServerVersions = {};
        }
        site.existingEnvironmentServerVersions[serverType] = window.drawerServerVersions[serverType];
    }
    syncExistingEnvironmentFromServers(site);
    emit(BUS_EVENTS.SITE_LIST_CHANGED);
    window.syncDesignOptionFields();
    window.updateNonGfNameBarCount();
    window.updateGfSummaryCount();
    if (AppState.currentStep === 2) { renderScopeMatrix(); syncMatrixToHiddenInputs(); }
}

export function removeVeeamServerFromSite(siteId, instanceId) {
    const site = getSiteById(siteId);
    if (!site || !Array.isArray(site.veeamServers)) return;
    site.veeamServers = site.veeamServers.filter((s) => s.instanceId !== instanceId);
    syncExistingEnvironmentFromServers(site);
    emit(BUS_EVENTS.SITE_LIST_CHANGED);
    window.syncDesignOptionFields();
    window.updateNonGfNameBarCount();
    window.updateGfSummaryCount();
    if (AppState.currentStep === 2) { renderScopeMatrix(); syncMatrixToHiddenInputs(); }
}

export function updateVeeamServerField(siteId, instanceId, field, value) {
    const site = getSiteById(siteId);
    if (!site || !Array.isArray(site.veeamServers)) return;
    const server = site.veeamServers.find((s) => s.instanceId === instanceId);
    if (!server) return;
    if (field === 'name') {
        server.name = String(value).trim() || VEEAM_SERVER_TYPES[server.type]?.label || 'Server';
    } else {
        server[field] = Math.max(0, parseInt(value, 10) || 0);
    }
    syncExistingEnvironmentFromServers(site);
    window.updateTotalInstances();
    window.updateWorkloadValidationState();
    window.syncNextStepAvailability();
}
